import React, { useEffect, useMemo, useRef, useState } from 'react'

import { CircularProgress, Grid, MenuItem } from '@material-ui/core'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'

import toastError from '../../errors/toastError'
import api from '../../services/api'
import { i18n } from '../../translate/i18n'
import { ButtonPrimary } from '../ButtonPrimary'
import { ButtonSecondary } from '../ButtonSecondary'
import ButtonWithSpinner from '../ButtonWithSpinner'
import SelectInput from '../SelectInput'
import UserAvatar from '../UserAvatar'
import Autocomplete from '@material-ui/lab/Autocomplete'
import TextField from '@material-ui/core/TextField'
import { useStyles } from './style'
import AutocompleteInput from '../AutocompleteInput'

// const filter = createFilterOptions({
//   trim: true
// })

const MessageForwardModal = ({
  modalOpen,
  onClose,
  ticketId,
  messageId,
  initialContact
}) => {
  const classes = useStyles()
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedContact, setSelectedContact] = useState(null)
  const [queues, setQueues] = useState([])
  const [selectedQueue, setSelectedQueue] = useState(null)

  const [loadingMore, setLoadingMore] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [searchParam, setSearchParam] = useState('')

  const isMounted = useRef(true)

  useEffect(() => {
    isMounted.current = true

    if (initialContact?.id !== undefined) {
      setOptions([initialContact])
      setSelectedContact(initialContact)
    }
    return () => {
      isMounted.current = false
    }
  }, [initialContact])

  const fetchContacts = React.useCallback(
    async (page = 1, reset = false, search = '') => {
      if (!modalOpen) return

      if (page === 1) {
        setLoading(true)
      } else {
        setLoadingMore(true)
      }

      try {
        const { data } = await api.get('/contacts/', {
          params: { searchParam: search, pageNumber: page }
        })

        if (!isMounted.current) return

        if (reset) {
          setOptions(data.contacts)
        } else {
          setOptions(prev => [...prev, ...data.contacts])
        }

        setHasMore(data.contacts.length === 20)
        setCurrentPage(page)
      } catch (err) {
        toastError(err)
      } finally {
        if (isMounted.current) {
          setLoading(false)
          setLoadingMore(false)
        }
      }
    },
    [modalOpen]
  )

  useEffect(() => {
    isMounted.current = true
    if (modalOpen) {
      fetchContacts(1, true, searchParam)
    }
    return () => {
      isMounted.current = false
    }
  }, [modalOpen, searchParam, fetchContacts])

  useEffect(() => {
    isMounted.current = true
    const fetchQueues = async () => {
      try {
        const { data } = await api.get('/queue?excludeBot=true')
        if (isMounted.current) setQueues(data)
      } catch (err) {
        toastError(err)
      }
    }
    fetchQueues()
    return () => {
      isMounted.current = false
    }
  }, [])

  const handleScroll = React.useCallback(
    event => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget

      if (
        scrollHeight - scrollTop <= clientHeight + 15 &&
        hasMore &&
        !loadingMore &&
        !loading
      ) {
        fetchContacts(currentPage + 1, false, searchParam)
      }
    },
    [currentPage, hasMore, loadingMore, loading, searchParam, fetchContacts]
  )

  const handleClose = () => {
    onClose()
    setSelectedContact(null)
    setSelectedQueue(null)
    setSearchParam('')
    setOptions([])
    setCurrentPage(1)
    setHasMore(true)
  }

  const handleForwardMessage = async (contactId, queueId) => {
    if (!contactId || !queueId) return
    setLoading(true)
    try {
      await api.post('/messages/forward', {
        contactId: selectedContact.id,
        ticketId,
        messageId,
        queueId: selectedQueue.id
      })

      if (isMounted.current) {
        onClose()
      }
    } catch (err) {
      toastError(err)
    } finally {
      if (isMounted.current) {
        setLoading(false)
      }
    }
  }

  return (
    <Dialog open={modalOpen} onClose={handleClose}>
      <DialogTitle id="form-dialog-title" className={classes.dialogTitle}>
        {i18n.t('messageOptionsMenu.forward')}
      </DialogTitle>
      <DialogContent dividers>
        <Grid style={{ width: 300 }} container spacing={2}>
          <Grid xs={12} item>
            <AutocompleteInput
              label="Escolha um contato"
              placeholder="Pesquise pelo nome do contato"
              options={options}
              loading={loading}
              value={selectedContact}
              onChange={(e, newValue) => setSelectedContact(newValue)}
              getOptionLabel={option =>
                `${option.name} - ${option.number || ''}`
              }
              onInputChange={(e, newInputValue, reason) => {
                if (reason === 'input') {
                  setSearchParam(newInputValue.toLowerCase())
                }
              }}
              ListboxProps={{
                onScroll: handleScroll,
                style: { maxHeight: 250, overflow: 'auto' }
              }}
              loadingMore={loadingMore}
            />
          </Grid>
          <Grid xs={12} item>
            <SelectInput
              label={i18n.t('transferTicketModal.fieldQueuePlaceholder')}
              value={selectedQueue?.id || ''}
              onChange={e => {
                const queueId = e.target.value
                const queue = queues.find(q => q.id === queueId)
                setSelectedQueue(queue || null)
              }}
            >
              <MenuItem value="">Selecione um departamento</MenuItem>
              {queues.map(queue => (
                <MenuItem key={queue.id} value={queue.id}>
                  {queue.name}
                </MenuItem>
              ))}
            </SelectInput>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <ButtonSecondary
          onClick={handleClose}
          label={i18n.t('newTicketModal.buttons.cancel')}
          disabled={loading}
        />
        <ButtonWithSpinner
          disabled={!selectedContact || !selectedQueue}
          onClick={() =>
            handleForwardMessage(selectedContact.id, selectedQueue.id)
          }
          loading={loading}
        >
          {i18n.t('messageOptionsMenu.forward')}
        </ButtonWithSpinner>
      </DialogActions>
    </Dialog>
  )
}

export default MessageForwardModal
